import { mapCatalogEntry, TOTAL_MAP_COUNT } from '@kenocyber/shared';

/**
 * Which background belongs to a map.
 *
 * ## Why a resolver and not twenty files
 *
 * Uma funcao que responde "o que desenho atras do mapa N", para o mapa e a arte
 * dele nunca serem duas decisoes. Os vinte azulejos existem — o pipeline emite
 * `background1..20` —, e este resolvedor deixou de ser um sitio a espera de
 * conteudo para passar a ser o sitio que o encaminha.
 *
 * The shape deliberately allows either a texture or a scene, because the
 * decision between a painted PNG and a rendered 3D backdrop has not been made
 * and this must not quietly make it.
 *
 * ## It cannot touch the simulation
 *
 * Nothing here is read by the hunt, appears in the snapshot, or reaches the
 * digest. A background is presentation: two players on the same map with
 * different art would still agree on every checkpoint, and a map whose art
 * fails to load is a map that still plays correctly.
 */

export type MapBackground =
  | {
      /** A painted backdrop, from the generated asset manifest. */
      readonly kind: 'texture';
      readonly assetId: string;
    }
  | {
      /** A rendered backdrop. No map uses one yet; the shape reserves it. */
      readonly kind: 'scene';
      readonly sceneId: string;
    }
  | {
      /**
       * The shared starfield every map falls back to.
       *
       * Not an error state and not a placeholder graphic: it is the look the
       * game ships with today, and a map without its own art simply keeps it.
       * Consistent across all nineteen, so an unfinished map never looks
       * broken - it looks like space.
       */
      readonly kind: 'starfield';
    };

/** The one fallback, named so nothing else invents a second one. */
export const STARFIELD_BACKGROUND: MapBackground = Object.freeze({ kind: 'starfield' });

/**
 * The background for a map.
 *
 * The twenty maps of the closed alpha each have a tile of their own, seamless
 * down its Y axis so it can scroll forever. Everything else - maps 21 to 50,
 * which are reserved, and anything out of range - falls back to the shared
 * starfield, which is the look the game ships with rather than a placeholder.
 *
 * ONE door. There is no second resolution path and no per-map special case:
 * the asset id is derived from the index exactly as `mapCode` is, so a map
 * either has a tile because the pipeline produced one, or it does not.
 */
export function backgroundForMap(mapIndex: number): MapBackground {
  if (!Number.isSafeInteger(mapIndex) || mapIndex < 1 || mapIndex > TOTAL_MAP_COUNT) {
    // Out of range is not a crash: a backdrop is the last thing that should
    // take an arena down. It falls back like anything else without art.
    return STARFIELD_BACKGROUND;
  }
  /*
   * **O id vem do CATALOGO** (gate V28I.BQ).
   *
   * Ele era derivado aqui por uma funcao local, a partir da mesma regra que o
   * catalogo usa para o mob e para o boss — uma segunda derivacao da mesma
   * verdade, e uma segunda oportunidade de as duas discordarem. Agora o
   * catalogo publica `backgroundAssetId` ao lado dos outros dois, e um mapa
   * sem azulejo diz `null` em vez de devolver um id que ninguem emitiu.
   */
  const assetId = mapCatalogEntry(mapIndex).backgroundAssetId;
  return assetId === null ? STARFIELD_BACKGROUND : { kind: 'texture', assetId };
}

/** The texture key the scene caches a backdrop under. Never the asset id. */
export function backgroundTextureKey(mapIndex: number): string {
  return `bg-${mapIndex}`;
}

/**
 * How fast a SKY backdrop drifts, in arena units per second. Downward, always.
 *
 * SLOWER than the far star layer, which moves at 9. That is the whole parallax
 * argument: a nebula is the most distant thing in the picture, so it must
 * appear to move less than the stars in front of it, and one that outran them
 * would read as the stars falling backwards. The first value here was 18 -
 * faster than the field it sits behind - which would have inverted the depth
 * cue the drift exists to create.
 */
export const BACKGROUND_DRIFT_UNITS_PER_SECOND = 5;

/**
 * How fast a GROUND backdrop drifts. Much faster, and for the same reason.
 *
 * A nebula is light-years away; a city is the surface the ship is flying over.
 * They sit at opposite ends of the depth stack, so giving them one speed gets
 * one of them wrong - and at 5 the city crawled, which reads as hovering
 * rather than as flying.
 *
 * Placed between the mid star layer (26) and the near one (74), which keeps
 * the whole stack monotonic: far stars, mid stars, the ground, then the near
 * streaking stars closest of all. Nothing about the starfield changes.
 *
 * One full cycle of the 1280-unit tile takes about half a minute, which is
 * long enough that the eye stops waiting for the repeat.
 */
export const GROUND_DRIFT_UNITS_PER_SECOND = 42;

/**
 * How a map's backdrop moves, and whether the shared starfield sits on it.
 *
 * ## Why these are two facts and not one
 *
 * They used to be a single boolean: a map was a "ground scene" or it was
 * not, and that one answer decided both the drift rate and whether the star
 * layers were drawn. It held for a while because every floor wanted fast and
 * no stars, and every sky wanted slow and stars.
 *
 * Map 15 breaks it. It is a nebula - unmistakably sky, so calling it a ground
 * scene would be a lie in the code - but it has to move quickly, because the
 * ship is passing THROUGH the cloud rather than looking at it from far away.
 * It also carries its own violet stars, painted into the master, so the
 * shared white field on top of them is redundant at best and a second,
 * disagreeing star population at worst.
 *
 * Two independent questions, so: two fields.
 */
export interface BackdropMotion {
  /** Downward drift of the backdrop tile, in arena units per second. */
  readonly driftUnitsPerSecond: number;
  /** Whether the shared starfield is drawn in front of this backdrop. */
  readonly starfield: boolean;
}

/** The default: something far away, with the star layers in front of it. */
const SKY: BackdropMotion = {
  driftUnitsPerSecond: BACKGROUND_DRIFT_UNITS_PER_SECOND,
  starfield: true,
};

/** A surface being flown over. Opaque, so the starfield would show through it. */
const GROUND: BackdropMotion = {
  driftUnitsPerSecond: GROUND_DRIFT_UNITS_PER_SECOND,
  starfield: false,
};

/**
 * How each map's backdrop moves. Anything absent is distant sky.
 *
 * A table rather than a rule, because there is no rule to derive it from:
 * what a map's art depicts is a design decision per map. Kept here, next to
 * the two rates, so the scene never has to learn what any map looks like.
 */
const BACKDROP_MOTION: ReadonlyMap<number, BackdropMotion> = new Map([
  [2, GROUND],
  [4, GROUND],
  [5, GROUND],
  [6, GROUND],
  [7, GROUND],
  [8, GROUND],
  [9, GROUND],
  [10, GROUND],
  [11, GROUND],
  [12, GROUND],
  [13, GROUND],
  [14, GROUND],
  [16, GROUND],
  [18, GROUND],
  [19, GROUND],
  [
    20,
    {
      /*
       * Her storm, and the ship is inside it rather than under it.
       *
       * Faster than an ordinary sky and slower than a floor, for the same
       * reason map 15 is: a nebula seen from far off should barely move,
       * and one the ship is passing through should not. It is still SKY -
       * calling it ground to buy the speed would be a lie in the code, and
       * that is exactly the distinction these two fields exist to keep.
       */
      driftUnitsPerSecond: 30,
      /*
       * Starless, because the master carries its own violet stars. The
       * shared white field over them would be a second population at a
       * second speed, which reads as two skies.
       */
      starfield: false,
    },
  ],
  [
    17,
    {
      /*
       * The fastest backdrop in the game, and faster than the ground rate.
       *
       * Every other floor is a surface being flown OVER at a plausible height.
       * This one is meant to read as speed itself - the bed streams past, and
       * because the scene adds this figure to every particle it emits, the
       * motes and the blooms in the air stream with it. One number moves the
       * whole picture.
       */
      driftUnitsPerSecond: 68,
      /*
       * Off. The bed fills the tile, so the shared star layers would be seen
       * through solid ground - and the map has its own falling lights.
       */
      starfield: false,
    },
  ],
  [
    15,
    {
      /*
       * Between the two rates, and closer to the ground than to the sky.
       *
       * At the distant rate the cloud barely moved, which read as the ship
       * hanging still. At the ground rate it would read as a surface. This is
       * a nebula the ship is flying through: fast enough to feel like travel,
       * short of fast enough to feel like altitude.
       */
      driftUnitsPerSecond: 24,
      /*
       * Off, and not because the cloud is opaque - it is not. The master
       * already carries five hundred violet stars of its own, and the shared
       * white field over them would be a second star population moving at a
       * different speed and disagreeing about colour.
       */
      starfield: false,
    },
  ],
]);

/** How map N's backdrop moves. Distant sky for anything not listed. */
export function backdropMotion(mapIndex: number): BackdropMotion {
  return BACKDROP_MOTION.get(mapIndex) ?? SKY;
}
/**
 * A population of animated things living on a map's ground.
 *
 * ## Scenery, and nothing else
 *
 * These are drawn between the backdrop and the game. They have no health,
 * cannot be targeted, cannot be hit, never enter the snapshot and never reach
 * the digest - a player whose browser failed to load one of these sheets plays
 * exactly the same hunt as everybody else. That is the line that keeps a
 * backdrop a backdrop.
 *
 * ## Why a table and not a scene full of conditionals
 *
 * The scene should never learn what any particular map looks like. It reads
 * this list, loads what it names and animates it; the day map 11 wants ash
 * drifting over it, that is a row here.
 */
export interface GroundLifeSpec {
  /** Manifest id of an ANIMATED asset. Loaded on demand, with the backdrop. */
  readonly assetId: string;
  /** How many of them are alive at once. Pooled: this is also the sprite count. */
  readonly count: number;
  readonly minScale: number;
  readonly maxScale: number;
  /**
   * Sideways crawl, in arena units per second. The sign is drawn per creature.
   *
   * Zero for anything rooted to the spot - a fire does not walk. Everything
   * scrolls DOWN at the map's own drift regardless, because it is lying on the
   * ground the ship is flying over.
   */
  readonly crawl: number;
  readonly alpha: number;
  /**
   * Turn each one to its own angle, even though it does not walk.
   *
   * Rotation used to be implied by crawl, on the reasoning that a thing which
   * stays put stays upright - true of a flame and false of an eye set in a
   * floor, which has no upright. It matters beyond looks for map 13: turning
   * the sprite turns the gaze with it, so eyes sharing one nine-frame sheet
   * end up looking in different directions at the same moment.
   */
  readonly spin?: boolean;
  /*
   * Keep turning, in radians per second. The direction is drawn per sprite.
   *
   * Different from `spin`, which sets one angle once and leaves it there.
   * That is right for an eye in a floor, which has no upright but is not
   * going anywhere; it is wrong for a ward, which is machinery and has to be
   * seen to run. A sheet can animate its own parts against each other - the
   * ward's script, nodes and star all counter-rotate within nine frames -
   * but it cannot turn the circle as a whole, because a rotation baked into
   * nine frames is nine visible steps.
   */
  readonly turn?: readonly [number, number];
  /**
   * Space these evenly down a band instead of scattering them.
   *
   * For anything big enough that two of them landing together would be seen
   * as a mistake. Everything on the ground moves at one speed, so an even
   * layout stays even forever - which is why this is a placement rule and not
   * a collision test. There is nothing to test against once they cannot meet.
   */
  readonly ladder?: boolean;
  /*
   * A second animated asset pinned to this one, which never rotates.
   *
   * It exists because of a shape that cannot be drawn any other way: a mark
   * standing inside a turning ring. Anything drawn into the ring's own
   * frames turns with the ring, and a symbol that spins is a pinwheel - it
   * stops reading as something that means anything.
   *
   * So it is a separate sprite sharing the first one's position, size and
   * scroll, and nothing else. It gets no rotation, no crawl of its own and
   * no placement of its own; it is not a second creature.
   */
  readonly companion?: string;
  /**
   * Playback rate range, as a multiple of the sheet's own frame rate.
   *
   * A random STARTING frame only spreads a population out once; they still
   * march at one speed forever, and a field of them eventually reads as a
   * mechanism. Giving each its own rate means no two ever come back into step.
   */
  readonly timeScale?: readonly [number, number];
}

/**
 * What lives on each map. Empty for every map that is just scenery.
 *
 * An effect that no map lists is a sheet the pipeline slices, verifies and
 * ships for nobody, so anything that leaves this table leaves with its master.
 * Two have: the fires that once stood in map 5's valley, and the golden
 * aureoles that hung over map 11 until the light behind them turned out to be
 * the better subject.
 *
 * The fire came back once, for map 12, and left again the same day: standing
 * flames on a floor already split by lava were a second fire competing with
 * the first. What a map wants is not always what it asked for, and the master
 * is in the session's scratchpad either way.
 */
const GROUND_LIFE: ReadonlyMap<number, readonly GroundLifeSpec[]> = new Map([
  [
    5,
    [
      /*
       * The maggots, and the scales are a size on SCREEN rather than a number.
       *
       * The master's cell is 200 units, so a scale of 1 would be a maggot as
       * long as two ships. These have been through both extremes: 22 to 45
       * units was a scatter of specks, and 65 to 357 put larvae half the
       * height of the arena across the valley. At 0.13 to 0.34 they run 26 to
       * 68 units - a third of the ship at their largest, which is the size at
       * which a field of them reads as infestation rather than as fauna.
       *
       * The sheet stays at 200 px per cell even though nothing now asks for
       * more than 68 of them. Detail costs nothing to have and cannot be
       * invented later: shrinking the master to match today's scale would make
       * the next request for bigger ones a re-render rather than a number.
       */
      { assetId: 'maggot', count: 26, minScale: 0.13, maxScale: 0.34, crawl: 13, alpha: 0.92 },
    ],
  ],
  [
    8,
    /*
     * Soul flames standing on the sanctum floor. They do not walk - a flame is
     * rooted to whatever is burning under it - so the crawl is zero and they
     * scroll with the ground like everything else lying on it.
     */
    [{ assetId: 'soulflame', count: 18, minScale: 0.28, maxScale: 0.7, crawl: 0, alpha: 0.95 }],
  ],
  [
    9,
    /*
     * Nodes socketed into the board. Bolted down, so no crawl - they scroll
     * with the board they are mounted on and nothing else.
     */
    [{ assetId: 'datanode', count: 14, minScale: 0.22, maxScale: 0.6, crawl: 0, alpha: 0.9 }],
  ],
  [
    10,
    /*
     * Sigils standing on the reliquary floor: an energy sphere with magic
     * circles turning around it, which is the one element of this map that is
     * better animated than painted. The background has static ones inlaid into
     * the stone; these are the ones that are still running.
     *
     * Rooted, like the sanctum flames - a sigil is inscribed on the floor and
     * does not walk - so the crawl is zero and they scroll with the ground.
     *
     * Twelve, not thirty. The floor is meant to read as elegant, and a room
     * full of these would read as a control panel.
     */
    [{ assetId: 'sigil', count: 12, minScale: 0.3, maxScale: 0.78, crawl: 0, alpha: 0.9 }],
  ],
  [
    13,
    /*
     * The eyes in the skin, and they are HERE rather than in the master
     * because an eye painted into a backdrop cannot look at anything. They do
     * not walk - an eye set in a floor is set in it - so the crawl is zero and
     * they scroll with the flesh they are part of.
     *
     * Many, and mostly small. One large eye is a landmark the player learns
     * the position of; forty small ones are a floor that is watching.
     */
    [
      {
        assetId: 'eye',
        count: 40,
        minScale: 0.12,
        maxScale: 0.42,
        crawl: 0,
        alpha: 1,
        spin: true,
        // Slow, and no two alike. The sheet sweeps a full circle over nine
        // frames; at a fifth of the rate that is a gaze that drifts rather
        // than one that scans.
        timeScale: [0.16, 0.42],
      },
    ],
  ],
  [
    19,
    /*
     * The wards, which were part of the background master until they had to
     * turn.
     *
     * Fourteen were cut into the tile; five are laid on it. A painted tile
     * cannot rotate at any price, so the choice was never how to animate the
     * master - it was whether a magic circle is terrain or a thing standing
     * on terrain. It is the second, and it always was.
     *
     * Rooted, so no crawl: a ward is inscribed on the floor, not walking
     * across it. Everything on the ground scrolls down with the map anyway.
     * The scales are a size on SCREEN - the cell is 288 units, so 0.55 to
     * 1.15 runs them from a ship-and-a-half wide to nearly half the arena,
     * which is the size at which one reads as something the floor was built
     * around rather than as a decal.
     */
    [
      {
        assetId: 'ward',
        count: 5,
        minScale: 0.55,
        maxScale: 1.15,
        crawl: 0,
        alpha: 0.92,
        spin: true,
        ladder: true,
        companion: 'glyph',
        /*
         * Visibly turning, each its own way, and this is the ONLY rotation on
         * the ward now.
         *
         * The sheet used to counter-rotate its script, nodes and star against
         * each other over nine frames. Nine frames is nine visible steps, and
         * the circles read exactly as what they were - a sprite flicking
         * between poses. The frames now carry a CHARGE instead, brightening
         * and dimming, which is a change nine steps can express without
         * looking stepped. All the turning is this number, applied to the
         * sprite every frame, which is as smooth as the display allows.
         *
         * A full turn in ten to twenty-two seconds: fast enough to be seen
         * turning while you look at it, slow enough never to read as a fan.
         */
        turn: [0.28, 0.62],
        timeScale: [0.6, 1.15],
      },
    ],
  ],
]);

/** What lives on map N. An empty list for every map that has nothing. */
export function groundLifeForMap(mapIndex: number): readonly GroundLifeSpec[] {
  return GROUND_LIFE.get(mapIndex) ?? [];
}

/**
 * What is in the AIR over a map: embers, ash, smoke.
 *
 * ## Particles rather than sprites, and that is the whole reason for a second
 * shape
 *
 * Ground life is a fixed population of animated bodies that lie on the terrain
 * and scroll with it. Air is the opposite: things that are BORN, drift, fade
 * and die, in numbers that change every second. A pool of sprites cannot do a
 * lifetime, and a spritesheet cannot do an alpha ramp - which is why a fire pit
 * as a nine-frame loop was the wrong tool for this and a particle emitter is
 * the right one.
 *
 * ## Still scenery
 *
 * Behind the game, exactly like the backdrop. Nothing here is an enemy, none of
 * it is in the snapshot, and none of it may ever be bright or busy enough to
 * compete with a projectile - a backdrop that swallowed a shot would be a
 * gameplay bug wearing an art costume.
 */
export interface AirParticleSpec {
  /** Which texture it is drawn from. The look is baked into the pixels. */
  readonly texture:
    | 'fire'
    | 'blood'
    | 'violet'
    | 'current'
    | 'gold'
    | 'ember'
    | 'spore'
    | 'sand'
    | 'crucifix'
    | 'spore-green'
    | 'petal-blue'
    | 'bloom-blue'
    | 'cloud-green'
    | 'cross'
    | 'cross-violet'
    | 'cross-gold'
    | 'cross-blue'
    | 'vent'
    | 'rune-blue'
    | 'halo-blue'
    | 'star-violet'
    | 'cloud';
  /**
   * How it is composited.
   *
   * `add` for anything that EMITS light - it accumulates past white where
   * particles overlap, which is what makes a cluster of embers read as a fire
   * rather than as a pile of orange discs. `normal` for anything merely lit by
   * something else, like smoke.
   */
  readonly blend: 'add' | 'normal';
  /** Where it draws. All of these are behind everything the player plays with. */
  readonly depth: number;
  /** Particles born per second across the whole visible area. */
  readonly rate: number;
  /** Milliseconds each one lives, min and max. */
  readonly lifeMs: readonly [number, number];
  readonly minScale: number;
  readonly maxScale: number;
  /** Opacity at birth. Every one of these fades to nothing as it dies. */
  readonly alpha: number;
  /**
   * Vertical drift in arena units per second, ON TOP of the map's own scroll.
   *
   * Negative rises. Fire is hot and climbs against the ground it came from; a
   * cinder has gone out and falls back towards it.
   *
   * This is a PERTURBATION, never the whole motion. The scene adds the map's
   * own scroll to every particle it emits, so all three stream past the ship
   * in the direction of travel and the numbers here only decide which of them
   * gets left behind fastest. Fire fights the flow hardest and so drifts by
   * slowest; a dead cinder falls with it and overtakes everything. That spread
   * is what reads as speed - a field all moving at one rate reads as a sheet.
   */
  readonly lift: number;
  /** Sideways wander, in arena units per second. */
  readonly sway: number;
}

/**
 * What is in the air on each map. Empty for every map with nothing in it.
 *
 * Map 5 is burning somewhere out of frame: fire carried over the valley, the
 * cinders it drops as it cools, and banks of smoke lying between them.
 *
 * ## The scales are SMALL on purpose
 *
 * An earlier pass answered "make them more visible" by enlarging them, which
 * was the wrong knob entirely: it produced big soft blobs, and a big soft blob
 * is fog. What makes a spark read is a small, very BRIGHT point with a glow
 * around it. The fire texture is 64 units across, so 0.35 is a twenty-two unit
 * spark - a quarter of the ship - and it is legible because it is intense, not
 * because it is large. Smoke is the only thing here allowed to be big.
 */
const AIR_PARTICLES: ReadonlyMap<number, readonly AirParticleSpec[]> = new Map([
  [
    5,
    [
      {
        texture: 'cloud',
        blend: 'normal',
        depth: -0.45,
        rate: 4,
        lifeMs: [9000, 15_000],
        minScale: 1.4,
        maxScale: 3.2,
        alpha: 0.3,
        lift: -6,
        sway: 8,
      },
      {
        texture: 'fire',
        blend: 'add',
        depth: -0.35,
        rate: 16,
        lifeMs: [3500, 6500],
        minScale: 0.1,
        maxScale: 0.22,
        alpha: 0.75,
        lift: 16,
        sway: 14,
      },
      {
        texture: 'fire',
        blend: 'add',
        depth: -0.25,
        rate: 14,
        lifeMs: [2200, 4200],
        minScale: 0.22,
        maxScale: 0.5,
        alpha: 1,
        lift: -12,
        sway: 18,
      },
    ],
  ],
  [
    6,
    /*
     * The blood field throws sparks of its own. Same brightness standard as
     * map 5's fire - a small, very intense point with a glow around it - and
     * the same rule about direction: the scene adds the map's scroll, so these
     * stream past the ship rather than hanging in front of it.
     */
    [
      {
        texture: 'blood',
        blend: 'add',
        depth: -0.35,
        rate: 18,
        lifeMs: [2600, 5000],
        minScale: 0.1,
        maxScale: 0.24,
        alpha: 0.8,
        lift: 12,
        sway: 12,
      },
      {
        texture: 'blood',
        blend: 'add',
        depth: -0.25,
        rate: 13,
        lifeMs: [2000, 3800],
        minScale: 0.24,
        maxScale: 0.52,
        alpha: 1,
        lift: -14,
        sway: 16,
      },
    ],
  ],
  [
    7,
    /*
     * Ash over the thornwood, as four-pointed white sparks. Same brightness
     * standard as the fire on map 5 and the blood on map 6 - a small intense
     * core with a glow - and the same rule about direction: the scene adds the
     * map's scroll, so they stream past rather than hang.
     */
    [
      {
        texture: 'cross',
        blend: 'add',
        depth: -0.35,
        rate: 15,
        lifeMs: [3000, 5600],
        minScale: 0.08,
        maxScale: 0.18,
        alpha: 0.65,
        lift: 10,
        sway: 13,
      },
      {
        texture: 'cross',
        blend: 'add',
        depth: -0.25,
        rate: 9,
        lifeMs: [2400, 4400],
        minScale: 0.2,
        maxScale: 0.42,
        alpha: 1,
        lift: -16,
        sway: 17,
      },
    ],
  ],
  [
    8,
    /*
     * Violet motes over the sanctum, on the same brightness standard as every
     * other map's sparks - a small intense core with a glow - and inheriting
     * the map's scroll by the same rule.
     */
    [
      {
        texture: 'violet',
        blend: 'add',
        depth: -0.35,
        rate: 24,
        lifeMs: [3200, 6000],
        minScale: 0.12,
        maxScale: 0.28,
        alpha: 1,
        lift: 9,
        sway: 12,
      },
      {
        texture: 'violet',
        blend: 'add',
        depth: -0.25,
        rate: 15,
        lifeMs: [2400, 4400],
        minScale: 0.3,
        maxScale: 0.62,
        alpha: 1,
        lift: -22,
        sway: 15,
      },
    ],
  ],
  [
    9,
    /*
     * Charge coming off the board. Same brightness standard as every other
     * map's sparks, in the machine's own blue, and inheriting the scroll.
     */
    [
      {
        texture: 'current',
        blend: 'add',
        depth: -0.35,
        rate: 26,
        lifeMs: [2800, 5200],
        minScale: 0.12,
        maxScale: 0.27,
        alpha: 1,
        lift: 11,
        sway: 14,
      },
      {
        texture: 'current',
        blend: 'add',
        depth: -0.25,
        rate: 16,
        lifeMs: [2200, 4000],
        minScale: 0.3,
        maxScale: 0.6,
        alpha: 1,
        lift: -20,
        sway: 16,
      },
    ],
  ],
  [
    10,
    /*
     * Arcane crosses over the reliquary, in the purple its inlays burn with.
     *
     * Same brightness standard as every map before it - a small intense core
     * with a glow - and the same rule about direction, since the scene adds the
     * map's own scroll to every one of these so they stream past rather than
     * hang in the air.
     *
     * Fewer and slower than the machine next door. This floor is meant to read
     * as elegant, and elegance is a matter of restraint: a blizzard of sparks
     * over pale marble reads as static on a screen.
     */
    [
      {
        texture: 'cross-violet',
        blend: 'add',
        depth: -0.35,
        rate: 14,
        lifeMs: [3400, 6200],
        minScale: 0.1,
        maxScale: 0.22,
        alpha: 1,
        lift: 8,
        sway: 10,
      },
      {
        texture: 'cross-violet',
        blend: 'add',
        depth: -0.25,
        rate: 8,
        lifeMs: [2600, 4800],
        minScale: 0.26,
        maxScale: 0.52,
        alpha: 1,
        lift: -18,
        sway: 14,
      },
    ],
  ],
  [
    11,
    /*
     * Gold over the cloud sea: four-pointed crosses and round points.
     *
     * Two ideas were tried here and removed. Feathers went because a feather
     * is only legible while it is large, and anything that large reads as
     * something the player is meant to shoot. Golden aureoles went with them:
     * a solid object hanging over the deck competed with the light coming
     * through it, and the light is the map.
     *
     * What is left is light. Same brightness standard as every map before it
     * - white core, saturated skirt - and the scene adds the map's own scroll
     * to each, so they stream past rather than hang.
     *
     * The two are sized apart on purpose. The crosses are the largest motes on
     * any map here - nothing solid remains up in this air to carry the eye, so
     * they carry it - and the round points stay small, because a big round one
     * is a blur and a small one is a spark. Their brightness comes from the
     * longer skirt the texture gives them, not from their size.
     */
    [
      {
        texture: 'gold',
        blend: 'add',
        depth: -0.35,
        rate: 26,
        lifeMs: [3000, 5600],
        minScale: 0.1,
        maxScale: 0.24,
        alpha: 1,
        lift: 10,
        sway: 13,
      },
      {
        texture: 'cross-gold',
        blend: 'add',
        depth: -0.25,
        rate: 13,
        lifeMs: [2600, 4800],
        minScale: 0.42,
        maxScale: 0.9,
        alpha: 1,
        lift: -19,
        sway: 16,
      },
    ],
  ],
  [
    12,
    /*
     * What comes off burning rock: red embers and yellow sparks.
     *
     * Same brightness standard as every map before it - white core,
     * saturated skirt - and the scene adds the map scroll to each, so they
     * stream past rather than hang.
     *
     * The red ones are the many and the small: a fissure throws far more
     * cooling cinders than it does live sparks, and the ratio is what stops
     * the air reading as a uniform orange haze.
     */
    [
      {
        texture: 'ember',
        blend: 'add',
        depth: -0.35,
        rate: 30,
        lifeMs: [2800, 5200],
        minScale: 0.09,
        maxScale: 0.2,
        alpha: 1,
        lift: 12,
        sway: 14,
      },
      {
        texture: 'gold',
        blend: 'add',
        depth: -0.25,
        rate: 14,
        lifeMs: [2200, 4200],
        minScale: 0.14,
        maxScale: 0.32,
        alpha: 1,
        lift: -20,
        sway: 17,
      },
    ],
  ],
  [
    13,
    /*
     * What comes off living tissue: pale spores and dark red flecks.
     *
     * Same brightness standard as every map before it - white core,
     * saturated skirt - and the scene adds the map scroll to each, so they
     * stream past rather than hang.
     *
     * DIMMER than any other map here, and that is the point. Nothing on this
     * floor emits: it is lit tissue, not lava or arcane inlay, so motes that
     * burned over it would be the only light source in a picture that is not
     * supposed to have one.
     */
    [
      {
        texture: 'spore',
        blend: 'add',
        depth: -0.35,
        rate: 20,
        lifeMs: [4000, 7200],
        minScale: 0.08,
        maxScale: 0.19,
        alpha: 0.55,
        lift: 9,
        sway: 15,
      },
      {
        texture: 'blood',
        blend: 'add',
        depth: -0.25,
        rate: 11,
        lifeMs: [2600, 4800],
        minScale: 0.12,
        maxScale: 0.28,
        alpha: 0.7,
        lift: -17,
        sway: 13,
      },
    ],
  ],
  [
    14,
    /*
     * Sand on the wind, and this map is the reason `lift` exists.
     *
     * Everything else in this table drifts: an ember climbs, a cinder falls
     * back, a spore hangs. These are DRIVEN. The lift is several times any
     * other value here, so the grains cross the arena in about a second and
     * the whole screen reads as moving faster than the ground under it -
     * which is what a wind blowing over a dune field looks like from above.
     *
     * Same brightness standard as every map before it, with the longer skirt
     * the texture gives this hue: nothing on this floor emits, so the grains
     * are the only bright thing in the frame and can afford to be.
     */
    [
      {
        texture: 'sand',
        blend: 'add',
        depth: -0.35,
        rate: 46,
        // Short lives, because they are gone off the bottom before a long
        // one would expire. A grain that outlived the screen is wasted work.
        lifeMs: [900, 1700],
        minScale: 0.07,
        maxScale: 0.15,
        alpha: 1,
        lift: 58,
        sway: 22,
      },
      {
        texture: 'sand',
        blend: 'add',
        depth: -0.25,
        rate: 22,
        lifeMs: [700, 1400],
        minScale: 0.16,
        maxScale: 0.3,
        alpha: 1,
        // Faster still, and nearer. The spread between the two layers is
        // what gives the wind depth instead of one sheet sliding past.
        lift: 88,
        sway: 26,
      },
    ],
  ],
  [
    15,
    /*
     * Crucifixes adrift in the nebula.
     *
     * They DESCEND WITH THE CLOUD, and the lift is what makes that exact.
     *
     * The scene adds the backdrop scroll to every particle it emits, so the
     * baseline here is already the nebula. A lift of zero therefore does not
     * mean still - it means moving at precisely the speed of the cloud behind
     * it, which is what these are: crosses carried along in the drift rather
     * than falling through it. The second layer is given a little of its own
     * so the two do not read as one pane of glass sliding past.
     *
     * An earlier pass split the lift either way and one of the layers rose
     * against the nebula. That reads as two unrelated things sharing a screen.
     *
     * The scale range is wide on purpose - a fifth of the ship to nearly
     * half of it - and the smallest is still large enough to be read as a
     * cross rather than as a speck. That is the difference between a size
     * VARIATION and a distance fog.
     */
    [
      {
        texture: 'crucifix',
        blend: 'add',
        depth: -0.35,
        rate: 5,
        lifeMs: [7000, 12_000],
        minScale: 0.2,
        maxScale: 0.34,
        alpha: 0.9,
        lift: 0,
        sway: 5,
      },
      {
        texture: 'crucifix',
        blend: 'add',
        depth: -0.25,
        rate: 3,
        lifeMs: [6000, 10_000],
        minScale: 0.38,
        maxScale: 0.72,
        alpha: 1,
        lift: 7,
        sway: 7,
      },
    ],
  ],
  [
    16,
    /*
     * Whatever is growing between the towers, and what comes off it.
     *
     * The banks are painted into the master as mist lying on the plain; these
     * are the part of it that has lifted. Drawn ADDITIVELY, unlike map 5's
     * smoke: that is lit from below by a fire, and this is lit from inside
     * itself, so where two banks overlap it should be brighter and not
     * merely thicker.
     *
     * Same brightness standard as every map before it, and the scene adds the
     * map scroll to each, so both stream past in the direction of travel.
     */
    [
      {
        texture: 'cloud-green',
        blend: 'add',
        depth: -0.45,
        rate: 7,
        lifeMs: [6000, 10_000],
        minScale: 0.8,
        maxScale: 1.9,
        /*
         * Raised here rather than in the master, and the reason is a number:
         * the painted mist already puts map 16 at 14.5% of the tile above
         * luminance 40 against a bound of 15. There is nothing left in the
         * backdrop budget, and there is no budget at all on what the scene
         * draws over it - so the banks got the intensity and the ground kept
         * the restraint.
         */
        alpha: 0.58,
        lift: 4,
        sway: 9,
      },
      {
        texture: 'spore-green',
        blend: 'add',
        depth: -0.3,
        rate: 26,
        lifeMs: [3200, 6000],
        minScale: 0.1,
        maxScale: 0.24,
        alpha: 1,
        lift: 9,
        sway: 14,
      },
      {
        texture: 'spore-green',
        blend: 'add',
        depth: -0.25,
        rate: 12,
        lifeMs: [2600, 4800],
        minScale: 0.26,
        maxScale: 0.5,
        alpha: 1,
        lift: -14,
        sway: 16,
      },
    ],
  ],
  [
    17,
    /*
     * Blue over the black roses: fine motes, and a few open flowers among
     * them.
     *
     * The blue is not decoration - it is the same colour as the light that
     * describes the flowers in the master, which is why the map holds
     * together. Take it away and the bed is a dark field with nothing to say
     * what shape anything on it is.
     *
     * Same brightness standard as every map before it, and the scene adds the
     * map scroll to each, so both stream past in the direction of travel.
     */
    [
      {
        texture: 'petal-blue',
        blend: 'add',
        depth: -0.35,
        rate: 30,
        lifeMs: [3400, 6400],
        minScale: 0.09,
        maxScale: 0.2,
        alpha: 1,
        lift: 8,
        sway: 13,
      },
      {
        /*
         * Crosses among the motes, on the same blue. Fewer and larger than the
         * points, so the two read as different things rather than as one field
         * at two sizes.
         */
        texture: 'cross-blue',
        blend: 'add',
        depth: -0.3,
        rate: 14,
        lifeMs: [3000, 5600],
        minScale: 0.2,
        maxScale: 0.48,
        alpha: 1,
        lift: 5,
        sway: 12,
      },
      {
        texture: 'bloom-blue',
        blend: 'add',
        depth: -0.25,
        rate: 11,
        lifeMs: [4200, 7600],
        /*
         * Small, and bright rather than big.
         *
         * These were three times this size and read as soft blue blobs - which
         * is the same mistake map 5's embers made, answered the same way: what
         * makes a mote carry is intensity, not area. A big soft shape is fog; a
         * small hard one with a lit core is a thing.
         */
        minScale: 0.13,
        maxScale: 0.26,
        alpha: 1,
        lift: -12,
        sway: 16,
      },
    ],
  ],
  [
    18,
    /*
     * What comes off a living shell: cold motes bled from the seams.
     *
     * They RISE, all of them, and that is the only thing in the frame saying
     * the plates are not a floor. Something under the armour is venting, so
     * the air over it moves the wrong way for a surface being flown across -
     * and the disagreement between that and the scroll is what makes the map
     * read as a body rather than as a plain of black stone.
     *
     * Same brightness standard as every map before it - white core, saturated
     * skirt - in the same cold blue the seams leak and the creatures see with.
     */
    [
      {
        texture: 'vent',
        blend: 'add',
        depth: -0.35,
        rate: 22,
        lifeMs: [3600, 6800],
        minScale: 0.07,
        maxScale: 0.17,
        alpha: 1,
        lift: -13,
        sway: 10,
      },
      {
        texture: 'vent',
        blend: 'add',
        depth: -0.25,
        rate: 8,
        lifeMs: [3000, 5400],
        minScale: 0.2,
        maxScale: 0.44,
        alpha: 0.85,
        lift: -24,
        sway: 14,
      },
    ],
  ],
  [
    19,
    /*
     * A great deal of blue, in four layers.
     *
     * This map was WHITE once, and its air was the only entry in this table
     * that was not a light - solid dark motes on a normal blend, because over
     * white stone there is no headroom above the background to add into. The
     * floor is black now and that inversion went with it: these are back on
     * the standard profile, a core running to white with a saturated skirt,
     * composited additively like every other map here.
     *
     * The RINGS are the exception, and deliberately so. They carry a deep
     * blue rather than a pale one, because they are the same object as the
     * wards turning on the floor beneath them - shed halos - and a pale ring
     * next to a saturated ward reads as a different substance. Deep neon is
     * a dark hue driven hard, not a dim one: the ring still runs to white in
     * the middle of its band.
     *
     * Four layers rather than two because the map was asked for a lot of it,
     * and rate alone does not give a lot - it gives a swarm at one size. What
     * reads as a great deal is several populations at different sizes and
     * speeds, which is also the only way the field gets any depth.
     *
     * All of them fall with the backdrop: the scene adds the drift to
     * everything it emits, so the air agrees with the stone about which way
     * the ship is going.
     */
    [
      {
        texture: 'rune-blue',
        blend: 'add',
        depth: -0.36,
        rate: 64,
        lifeMs: [2600, 4800],
        minScale: 0.05,
        maxScale: 0.12,
        alpha: 1,
        lift: 20,
        sway: 16,
      },
      {
        texture: 'rune-blue',
        blend: 'add',
        depth: -0.3,
        rate: 30,
        lifeMs: [1800, 3400],
        minScale: 0.13,
        maxScale: 0.26,
        alpha: 1,
        lift: 46,
        sway: 22,
      },
      {
        texture: 'cross-blue',
        blend: 'add',
        depth: -0.28,
        rate: 16,
        lifeMs: [2200, 3800],
        minScale: 0.16,
        maxScale: 0.34,
        alpha: 0.95,
        lift: 30,
        sway: 18,
      },
      {
        texture: 'halo-blue',
        blend: 'add',
        depth: -0.24,
        rate: 13,
        lifeMs: [2400, 4200],
        minScale: 0.24,
        maxScale: 0.54,
        alpha: 0.9,
        lift: 34,
        sway: 20,
      },
    ],
  ],
  [
    20,
    /*
     * Everything she burns with, in four layers.
     *
     * Four rather than two because the map was asked to be the most special
     * of the twenty, and rate alone does not give that - it gives a swarm at
     * one size. Several populations at different sizes and speeds is what
     * reads as a lot of something, and it is also the only way the field in
     * front of the storm gets any depth of its own.
     *
     * All on the standard profile: a core running to white with the violet
     * around it, composited additively, falling with the storm behind them.
     */
    [
      {
        texture: 'violet',
        blend: 'add',
        depth: -0.36,
        rate: 58,
        lifeMs: [2600, 5000],
        minScale: 0.05,
        maxScale: 0.12,
        alpha: 1,
        lift: 18,
        sway: 16,
      },
      {
        texture: 'violet',
        blend: 'add',
        depth: -0.3,
        rate: 26,
        lifeMs: [1900, 3600],
        minScale: 0.14,
        maxScale: 0.28,
        alpha: 1,
        lift: 44,
        sway: 22,
      },
      {
        texture: 'star-violet',
        blend: 'add',
        depth: -0.27,
        rate: 15,
        lifeMs: [2200, 4000],
        minScale: 0.18,
        maxScale: 0.4,
        alpha: 0.95,
        lift: 28,
        sway: 18,
      },
      {
        texture: 'star-violet',
        blend: 'add',
        depth: -0.23,
        rate: 6,
        lifeMs: [2600, 4400],
        minScale: 0.22,
        maxScale: 0.5,
        alpha: 0.9,
        lift: 34,
        sway: 20,
      },
    ],
  ],
]);

/**
 * Lightning over a map, as weather.
 *
 * ## This is not a beam, and the distinction is the whole point
 *
 * This project forbids beams, rays and drawn lines absolutely - a shot is
 * always a discrete projectile, and anything that replaced a sequence of
 * them with a line has been removed on sight. That rule is about the path
 * between a CANNON and its TARGET, and it exists because a line there lies
 * about how the game works.
 *
 * A storm in the backdrop is a different object entirely. It is drawn behind
 * everything, it is not attached to the ship or to any enemy, it deals no
 * damage, it never enters the snapshot and it never reaches the digest. A
 * player whose browser failed to draw it plays exactly the same hunt.
 *
 * If this ever grows a target, a source, or a number attached to it, it has
 * stopped being weather and the rule applies again.
 */
export interface LightningSpec {
  /**
   * How many bolts the storm runs, each on a clock of its own.
   *
   * Independent, deliberately. One channel firing on one timer is a signal
   * lamp; several running separately drift in and out of phase, sometimes
   * overlap and sometimes leave a long gap, and that irregularity is what a
   * storm actually is. Nothing coordinates them and nothing should.
   */
  readonly bolts: number;
  /** Shortest and longest wait between strikes, in milliseconds. Per bolt. */
  readonly delayMs: readonly [number, number];
  /** The bolt colour, as a CSS colour string. */
  readonly hue: string;
  /** How long one strike lasts, flicker and all. */
  readonly durationMs: number;
}

/**
 * Which maps have weather. Empty for every map with a still sky.
 *
 * The interval is deliberately long and deliberately RANDOM. A bolt on a
 * fixed timer becomes a metronome the player starts counting, and a frequent
 * one becomes wallpaper; several seconds of nothing is what makes the strike
 * an event rather than an effect.
 */
const LIGHTNING: ReadonlyMap<number, LightningSpec> = new Map([
  [
    15,
    {
      bolts: 3,
      delayMs: [6000, 13_000] as const,
      hue: '#a24aff',
      durationMs: 620,
    },
  ],
  [
    20,
    {
      /*
       * Fourteen, against the three of map 15, and the difference is a kind
       * of weather rather than a bigger number.
       *
       * The interval is per BOLT. Fourteen channels at four to ten seconds
       * each put a strike somewhere about twice a second, and each lasts
       * 680 ms - so this sky is almost never without one, which is the point:
       * map 15 broods and this one is continuous. Nothing coordinates them, so
       * they still drift in and out of phase and no two rounds look alike.
       *
       * Each bolt builds its own texture from its index, so fourteen of them
       * are fourteen shapes and not one shape drawn fourteen times.
       *
       * Worth knowing rather than hidden: a bolt is additive and this many
       * means the arena is lit more often than it is not. They are thin, brief
       * and behind the game at depth -0.2, so nothing is ever covered at the
       * moment it matters - but if the map ever reads as too bright in play,
       * this count is the first number to look at.
       */
      bolts: 14,
      delayMs: [4000, 10_000] as const,
      hue: '#9b45ff',
      durationMs: 680,
    },
  ],
]);

/**
 * Shooting stars over a map.
 *
 * Sibling of `LightningSpec` and deliberately NOT the same type. A bolt is
 * fixed in place and flickers where it stands; a star crosses the whole
 * arena and has a heading. Folding them together would give one shape with
 * two halves that are never both used.
 */
export interface ShootingStarSpec {
  /** How many streaks run, each on a clock of its own. */
  readonly count: number;
  /** Shortest and longest wait between passes, in milliseconds. Per streak. */
  readonly delayMs: readonly [number, number];
  /** How long one crossing takes. */
  readonly durationMs: number;
  /**
   * The heading every one of them travels along, in radians.
   *
   * ONE angle for all of them, and that is the point. Stars that fanned out
   * would read as fireworks going off; a shower is a set of parallel tracks,
   * because everything in it is falling through the same sky the same way.
   */
  readonly angle: number;
  readonly hue: string;
  /*
   * Smallest and largest a single streak is drawn at. Optional.
   *
   * The texture is one size for every map, so this is the only lever a map
   * has over how big its shower reads. Left out, a shower gets the size map
   * 17 was tuned at, which is what every existing caller expects.
   */
  readonly scale?: readonly [number, number];
}

/** Which maps have a shower. Empty for every map with an ordinary sky. */
const SHOOTING_STARS: ReadonlyMap<number, ShootingStarSpec> = new Map([
  [
    17,
    {
      /*
       * Nine, each on its own clock. The count is what decides whether the sky
       * reads as a shower or as the occasional star: at six there were long
       * stretches with nothing crossing, and a shower is defined by never quite
       * being empty.
       */
      count: 14,
      delayMs: [6000, 9000] as const,
      durationMs: 1400,
      // Down and to the right, at about a third of a turn from vertical.
      angle: Math.PI * 0.32,
      hue: '#5a9cff',
    },
  ],
  [
    20,
    {
      /*
       * The only map with weather AND a shower, and that is the point of it.
       *
       * The two are independent tables on purpose, so nothing had to be
       * generalised to put both on one map: a bolt stands still and flickers,
       * a streak crosses the arena on a heading, and the last map simply
       * appears in both lists.
       */
      /*
       * Twenty-seven, and small.
       *
       * Count and size trade against each other: at twelve and full size
       * the sky read as a dozen comets, which is an event happening rather
       * than weather. Small and many is a SHOWER - no single one of them
       * asks to be watched, and the sky is never quite empty.
       */
      count: 27,
      scale: [0.38, 0.8] as const,
      delayMs: [5000, 8500] as const,
      durationMs: 1300,
      // Down and to the LEFT, against map 17's shower, so the two never look
      // like the same sky reused.
      angle: Math.PI * 0.68,
      hue: '#b070ff',
    },
  ],
]);

/**
 * A pulse of energy running a right-angled path across the arena.
 *
 * The third sibling of `LightningSpec` and `ShootingStarSpec`, and again its
 * own type rather than a field added to theirs. A bolt stands still, a star
 * travels one heading, and this TURNS - it needs a path, a drawing phase and a
 * fade, none of which the other two have any use for.
 *
 * It is scenery under the same rule as the rest of this block: it costs no
 * damage, enters no snapshot and reaches no digest. A browser that failed to
 * draw it plays exactly the same hunt.
 */
export interface EnergyTraceSpec {
  /** How many run, each on a clock of its own. */
  readonly count: number;
  /** Shortest and longest wait between runs, in milliseconds. Per trace. */
  readonly delayMs: readonly [number, number];
  /** How long the head takes to draw the whole path. */
  readonly drawMs: number;
  /** How long the finished path stands before it starts to go. */
  readonly holdMs: number;
  /** How long it takes to fade out once it has stood. */
  readonly fadeMs: number;
  /**
   * Fewest and most straight runs in one path.
   *
   * Two is the minimum that can be called a path: one run and a turn into
   * another. A single straight line is a shooting star, and there is already a
   * type for those.
   */
  readonly legs: readonly [number, number];
  readonly hue: string;
}

/**
 * Which maps carry current. Empty for every map whose floor is inert.
 *
 * Only the machined deck, and only because the deck is the reason it reads:
 * the plates are laid out on right angles, so a pulse that turns on right
 * angles looks like it is following something that was built. The same effect
 * over rock or flesh would look like a screensaver.
 */
const ENERGY_TRACES: ReadonlyMap<number, EnergyTraceSpec> = new Map([
  [
    18,
    {
      /*
       * Seven, each on a clock of its own.
       *
       * The count is what decides whether the deck reads as live or as
       * occasionally lit: at five there were long stretches with nothing
       * running anywhere, and a powered floor is defined by rarely being idle.
       * Nothing coordinates them, so they drift in and out of phase - sometimes
       * two turn across each other, sometimes the deck goes quiet.
       */
      count: 7,
      delayMs: [3500, 9000] as const,
      drawMs: 1500,
      holdMs: 240,
      fadeMs: 780,
      legs: [2, 4] as const,
      // Barely tinted. The deck's own strips are white and these are the same
      // current; a coloured pulse would read as a second, unrelated system.
      hue: '#e4f2ff',
    },
  ],
]);

/** The shower over map N, or null if nothing is falling through it. */
export function shootingStarsForMap(mapIndex: number): ShootingStarSpec | null {
  return SHOOTING_STARS.get(mapIndex) ?? null;
}
/** What runs across the floor of map N, or null if nothing does. */
export function energyTracesForMap(mapIndex: number): EnergyTraceSpec | null {
  return ENERGY_TRACES.get(mapIndex) ?? null;
}
/** The weather over map N, or null if its sky is still. */
export function lightningForMap(mapIndex: number): LightningSpec | null {
  return LIGHTNING.get(mapIndex) ?? null;
}
/** What is in the air on map N. An empty list for every map with nothing. */
export function airParticlesForMap(mapIndex: number): readonly AirParticleSpec[] {
  return AIR_PARTICLES.get(mapIndex) ?? [];
}

/**
 * Whether the shared starfield is drawn in front of map N.
 *
 * This replaced `isGroundScene`, which was only ever consulted to answer
 * exactly this question and had begun to answer it about a map that is not
 * ground at all.
 */
export function showsStarfield(mapIndex: number): boolean {
  return backdropMotion(mapIndex).starfield;
}

/** How fast map N's backdrop drifts, in arena units per second. */
export function backgroundDriftForMap(mapIndex: number): number {
  return backdropMotion(mapIndex).driftUnitsPerSecond;
}
